import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import AuthLayout from '../../components/auth/AuthLayout'
import FormInput from '../../components/auth/FormInput'
import FormButton from '../../components/auth/FormButton'
import ToastNotification from '../../components/ToastNotification'
import { motion } from 'framer-motion'

export default function RegisterPage() {
  const { theme } = useTheme()
  const { register } = useAuth()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  })
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setToastMessage('Mật khẩu không khớp!')
      setToastType('error')
      setShowToast(true)
      return
    }

    if (formData.password.length < 6) {
      setToastMessage('Mật khẩu phải có ít nhất 6 ký tự!')
      setToastType('error')
      setShowToast(true)
      return
    }

    try {
      setLoading(true)
      const result = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        password_confirm: formData.confirmPassword,
        phone: formData.phone,
      })

      if (result.success) {
        setToastMessage('Đăng ký thành công! Tài khoản của bạn đang chờ Admin duyệt.')
        setToastType('success')
        setShowToast(true)
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      } else {
        setToastMessage(result.error || 'Đăng ký thất bại. Vui lòng thử lại!')
        setToastType('error')
        setShowToast(true)
      }
    } catch (error) {
      setToastMessage('Đăng ký thất bại. Vui lòng thử lại!')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  return (
    <>
      <ToastNotification
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
      
      <AuthLayout
        title="Đăng Ký Tài Khoản"
        subtitle="Gia nhập cộng đồng đào tạo bóng đá chuyên nghiệp"
      >
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-5"
        >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Họ và tên"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Nguyễn Văn A"
            required
          />

          <FormInput
            label="Số điện thoại"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="0123 456 789"
            required
          />
        </div>

        <FormInput
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="your@email.com"
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Mật khẩu"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Tối thiểu 6 ký tự"
            required
            minLength={6}
          />

          <FormInput
            label="Xác nhận mật khẩu"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Nhập lại mật khẩu"
            required
          />
        </div>

        <div className={`flex items-start gap-3 rounded-xl p-4 border ${theme === 'dark'
          ? 'border-gold-500/30 bg-black/30'
          : 'border-gold-300 bg-gold-50'}`}>
          <input
            type="checkbox"
            required
            className="w-4 h-4 mt-1 text-gold-600 rounded focus:ring-gold-500"
          />
          <label className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Tôi đồng ý với{' '}
            <Link to="/terms" className="font-medium text-gold-500 hover:underline">Điều khoản sử dụng</Link>
            {' '}và{' '}
            <Link to="/privacy" className="font-medium text-gold-500 hover:underline">Chính sách bảo mật</Link>
          </label>
        </div>

        <div className={`rounded-xl p-4 border-2 ${theme === 'dark'
          ? 'border-gold-500/50 bg-gold-500/10'
          : 'border-gold-400 bg-gold-50'}`}>
          <p className={`text-sm md:text-base font-medium text-center ${theme === 'dark' ? 'text-gold-300' : 'text-gold-700'}`}>
            Bạn Vui Lòng Mua Khóa Học Để Tài Khoản Được Duyệt.
          </p>
        </div>

        <FormButton 
          className="w-full text-lg py-3 rounded-xl shadow-lg"
          disabled={loading}
        >
          {loading ? 'Đang xử lý...' : 'Đăng Ký Ngay'}
        </FormButton>

        <div className={`text-center text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          Đã có tài khoản?{' '}
          <Link to="/login" className="font-semibold text-gold-500 hover:underline">
            Đăng nhập
          </Link>
        </div>
      </motion.form>
    </AuthLayout>
    </>
  )
}

